import { useState, useEffect, useCallback } from 'react'
import { AppState, ModuleId, SubSkillProgress } from '../types'
import {
  ALL_QUESTIONS_CORE,
  MODULES,
  migrateLevelId,
} from '../data/questionLoader'
import {
  GroupId,
  StartingPeriod,
  getStartingLevelForPeriod,
} from '../config/systemConfig'
import {
  updateQuestionState,
  calculateMasteryPercentage,
  getModuleStats,
} from '../utils/knowledgeStateEngine'
import { computeSubSkillMastery } from '../utils/adaptiveEngine'
import { getPlacementKey } from '../utils/placementEngine'
import {
  resolvePhaseByDate,
  getAutoStartingLevel,
} from '../utils/dateBasedPlacement'
import {
  STORAGE_KEY,
  XP_PER_LEVEL,
  XP_REWARDS,
  DEFAULT_MASTERY_BY_DIFFICULTY,
  SESSION_CONFIG,
} from '../constants'
import { getLevelTier } from '../config/levels'

const INITIAL_STATE: AppState = {
  stats: {
    name: 'Djayden',
    xp: 0,
    level: 1,
  },
  progress: {
    Metriek: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    Getalbegrip: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    TijdGeld: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    SpellingWW: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    SpellingRegel: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    Taalbegrip: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    Studievaardig: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
    Topo: {
      masteryPercentage: 0,
      masteryByDifficulty: { ...DEFAULT_MASTERY_BY_DIFFICULTY },
      status: 'learning',
      cheatSheetViewed: false,
      questionsAnswered: [],
      recentQuestions: [],
      reviewNeededCount: 0,
      subSkills: {},
    },
  },
  questionStates: {},
  pendingLevelUp: undefined,
  placementResults: {},
}

export function useAppStore() {
  const [state, setState] = useState<AppState>(INITIAL_STATE)
  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedGroup, setSelectedGroupRaw] = useState<GroupId>(6)

  // manualPeriod is null if using auto-detection (default), or set if overridden
  const [manualPeriod, setManualPeriod] = useState<StartingPeriod | null>(null)

  // Track group changes to trigger session regeneration
  const [groupVersion, setGroupVersion] = useState(0)

  // Load state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    const storedGroup = localStorage.getItem(`${STORAGE_KEY}_group`)
    const storedPeriod = localStorage.getItem(`${STORAGE_KEY}_period`)

    if (storedGroup) {
      setSelectedGroupRaw(JSON.parse(storedGroup) as GroupId)
    }
    if (storedPeriod) {
      setManualPeriod(JSON.parse(storedPeriod) as StartingPeriod)
    }

    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.stats && parsed.progress) {
          // Migrate legacy data
          Object.keys(parsed.progress).forEach((moduleId) => {
            if (parsed.progress[moduleId].status === 'locked') {
              parsed.progress[moduleId].status = 'learning'
            }
            if (!parsed.progress[moduleId].masteryByDifficulty) {
              parsed.progress[moduleId].masteryByDifficulty = {
                ...DEFAULT_MASTERY_BY_DIFFICULTY,
              }
            }
            if (!parsed.progress[moduleId].subSkills) {
              parsed.progress[moduleId].subSkills = {}
            }
            delete parsed.progress[moduleId].uncertainCount
            delete parsed.progress[moduleId].overconfidentCount
          })

          if (!parsed.questionStates) parsed.questionStates = {}
          if (!parsed.pendingLevelUp) parsed.pendingLevelUp = undefined
          if (!parsed.placementResults) parsed.placementResults = {}

          // Migrate legacy question states
          Object.keys(parsed.questionStates).forEach((qId) => {
            const qs = parsed.questionStates[qId]
            if (qs) {
              if (qs.status === 'master') {
                if (qs.attempts < 3 || qs.correctAttempts < 3) {
                  qs.status = 'practiced'
                  qs.masteryPoints = 0.6
                } else {
                  qs.status = 'mastered'
                  qs.masteryPoints = 1.0
                }
              } else if (qs.status === 'investigating') {
                qs.status = 'learning'
                qs.masteryPoints = 0.2
              }
              if (qs.difficulty !== undefined) {
                qs.difficulty = migrateLevelId(qs.difficulty)
              }
            }
          })

          // Migrate legacy difficulty keys
          Object.keys(parsed.progress).forEach((moduleId) => {
            const mod = parsed.progress[moduleId]
            if (mod.masteryByDifficulty) {
              const migrated: Record<number, number> = {}
              Object.entries(mod.masteryByDifficulty).forEach(([key, val]) => {
                const oldId = Number(key)
                const newId = migrateLevelId(oldId)
                migrated[newId] = (migrated[newId] ?? 0) + (val as number)
              })
              mod.masteryByDifficulty = migrated
            }
          })

          delete parsed.stats.confidenceIndex
          delete parsed.recentConfidenceScores
          delete parsed.errorLog

          setState(parsed)
          console.log('✅ State loaded from localStorage')
        }
      } catch (e) {
        console.error('❌ Failed to parse stored state', e)
      }
    }
    setIsInitialized(true)
  }, [])

  // Save state to localStorage on change
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) {
        console.error('❌ Failed to save state to localStorage', e)
      }
    }
  }, [state, isInitialized])

  // Save group and period selection
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(
        `${STORAGE_KEY}_group`,
        JSON.stringify(selectedGroup),
      )
    }
  }, [selectedGroup, isInitialized])

  useEffect(() => {
    if (isInitialized) {
      if (manualPeriod) {
        localStorage.setItem(
          `${STORAGE_KEY}_period`,
          JSON.stringify(manualPeriod),
        )
      } else {
        localStorage.removeItem(`${STORAGE_KEY}_period`)
      }
    }
  }, [manualPeriod, isInitialized])

  // Group switching
  const setSelectedGroup = useCallback((group: GroupId) => {
    setSelectedGroupRaw(group)
    setGroupVersion((v) => v + 1)
    console.log(`🔄 Group switched to ${group}`)
  }, [])

  // Manual period switching (override)
  const setStartingPeriod = useCallback((period: StartingPeriod | null) => {
    setManualPeriod(period)
    setGroupVersion((v) => v + 1)
    console.log(
      `🔄 Starting period set to ${period || 'AUTO'} - context updated`,
    )
  }, [])

  // Computed effective period (for UI display)
  const startingPeriod = manualPeriod || resolvePhaseByDate()

  const markCheatSheetViewed = (moduleId: ModuleId) => {
    setState((prev) => ({
      ...prev,
      progress: {
        ...prev.progress,
        [moduleId]: {
          ...prev.progress[moduleId],
          cheatSheetViewed: true,
        },
      },
    }))
  }

  const recordAnswer = (
    moduleId: ModuleId,
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
  ) => {
    setState((prev) => {
      const question = ALL_QUESTIONS_CORE.find((q) => q.id === questionId)
      const difficulty = question?.difficulty
      const subSkill = question?.subSkill

      const currentQuestionState = prev.questionStates[questionId]
      const newQuestionState = updateQuestionState(
        currentQuestionState,
        questionId,
        isCorrect,
        difficulty,
      )

      const newQuestionStates = {
        ...prev.questionStates,
        [questionId]: newQuestionState,
      }

      const currentModule = prev.progress[moduleId]

      const masteryPercentage = calculateMasteryPercentage(
        moduleId,
        newQuestionStates,
        undefined,
        selectedGroup,
      )

      const masteryByDifficulty = { ...currentModule.masteryByDifficulty }
      if (difficulty !== undefined) {
        masteryByDifficulty[difficulty] = calculateMasteryPercentage(
          moduleId,
          newQuestionStates,
          difficulty,
          selectedGroup,
        )
      }

      const newSubSkills = { ...currentModule.subSkills }
      if (subSkill) {
        newSubSkills[subSkill] = computeSubSkillMastery(
          moduleId,
          subSkill,
          newQuestionStates,
          selectedGroup,
        )
      }

      const moduleStats = getModuleStats(
        moduleId,
        newQuestionStates,
        selectedGroup,
      )

      const newRecentQuestions = [
        questionId,
        ...currentModule.recentQuestions.filter((id) => id !== questionId),
      ].slice(0, SESSION_CONFIG.RECENT_QUESTIONS_LIMIT)

      const newQuestionsAnswered = currentModule.questionsAnswered.includes(
        questionId,
      )
        ? currentModule.questionsAnswered
        : [...currentModule.questionsAnswered, questionId]

      const newStatus = masteryPercentage >= 95 ? 'mastered' : 'learning'

      let xpGain = XP_REWARDS.EFFORT
      if (isCorrect && difficulty !== undefined) {
        const tier = getLevelTier(difficulty)
        switch (tier) {
          case 'hard':
            xpGain = XP_REWARDS.CORRECT_HARD
            break
          case 'medium':
            xpGain = XP_REWARDS.CORRECT_MEDIUM
            break
          case 'easy':
          default:
            xpGain = XP_REWARDS.CORRECT_EASY
            break
        }
      } else if (isCorrect) {
        xpGain = XP_REWARDS.CORRECT_EASY
      }

      const newXP = prev.stats.xp + xpGain
      const oldLevel = prev.stats.level
      const newLevel = Math.floor(newXP / XP_PER_LEVEL) + 1
      const pendingLevelUp =
        newLevel > oldLevel ? newLevel : prev.pendingLevelUp

      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: newXP,
          level: newLevel,
        },
        progress: {
          ...prev.progress,
          [moduleId]: {
            ...currentModule,
            masteryPercentage,
            masteryByDifficulty,
            status: newStatus,
            recentQuestions: newRecentQuestions,
            reviewNeededCount: moduleStats.reviewNeeded,
            questionsAnswered: newQuestionsAnswered,
            subSkills: newSubSkills,
          },
        },
        questionStates: newQuestionStates,
        pendingLevelUp,
      }
    })
  }

  const clearPendingLevelUp = () => {
    setState((prev) => ({
      ...prev,
      pendingLevelUp: undefined,
    }))
  }

  const devJumpToLevel = (targetLevel: number) => {
    setState((prev) => {
      const xpNeeded = (targetLevel - 1) * XP_PER_LEVEL
      return {
        ...prev,
        stats: {
          ...prev.stats,
          xp: xpNeeded,
          level: targetLevel,
        },
      }
    })
  }

  const devResetProgress = () => {
    localStorage.removeItem(STORAGE_KEY)
    setState(INITIAL_STATE)
    setGroupVersion((v) => v + 1)
  }

  const savePlacementResult = useCallback(
    (result: import('../types').PlacementResult) => {
      setState((prev) => {
        const key = getPlacementKey(result.moduleId, result.group as GroupId)
        return {
          ...prev,
          placementResults: {
            ...prev.placementResults,
            [key]: result,
          },
        }
      })
    },
    [],
  )

  /**
   * Get the starting level for a module+group.
   * Priority:
   * 1) Placement test result (if exists)
   * 2) Manual override (if set)
   * 3) Auto-detected date-based level
   */
  const getStartingLevel = useCallback(
    (
      moduleId: import('../types').ModuleId,
      group: GroupId,
    ): number | undefined => {
      // 1. Check for per-module placement test result
      const key = getPlacementKey(moduleId, group)
      const placementResult = state.placementResults?.[key]
      if (placementResult) {
        return placementResult.startingLevelId
      }

      // 2. Check for manual override
      if (manualPeriod) {
        return getStartingLevelForPeriod(group, manualPeriod)
      }

      // 3. Use auto-detected date-based level
      return getAutoStartingLevel(group)
    },
    [state.placementResults, manualPeriod],
  )

  const hasPlacement = useCallback(
    (moduleId: import('../types').ModuleId, group: GroupId): boolean => {
      const key = getPlacementKey(moduleId, group)
      return !!state.placementResults?.[key]
    },
    [state.placementResults],
  )

  return {
    state,
    isInitialized,
    selectedGroup,
    setSelectedGroup,
    startingPeriod, // Effective period (Auto or Manual)
    manualPeriod, // Raw manual state
    setStartingPeriod, // Setter for manual override
    groupVersion,
    markCheatSheetViewed,
    recordAnswer,
    clearPendingLevelUp,
    devJumpToLevel,
    devResetProgress,
    savePlacementResult,
    getStartingLevel,
    hasPlacement,
  }
}
